import React, {useEffect, useState, useRef} from 'react';
import {useGlobalContext} from "../context/GlobalState";
import Grid from '@mui/material/Grid2';
import {
    Box,
    Divider,
    TextField,
    Typography,
    MenuItem,
    InputAdornment,
    Autocomplete,
    IconButton,
    ListItem,
    Button,
    Paper,
    List,
    Skeleton, 
    ListItemButton
} from '@mui/material';
import {

    getBlacklistDestAccounts,
    getBlacklistedRoutings,
    getCheckPreviewData
    // getTrueChecksBlacklistedRoutings
} from "../services/Request";
import {
    updateCustomerConfigs,
    addBlacklistDestAccount,
    removeBlacklistDestAccount,
    addBlacklistedRouting,
    addTrueChecksBlacklistedRouting,
    removeBlacklistedRouting,
    removeTrueChecksBlacklistedRouting
} from "../services/Update";
import LoadingButton from "@mui/lab/LoadingButton";
import dayjs from "dayjs";
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {BranchSources} from "../components/BranchSources";
import {Snackbar} from "../components/Snackbar";

// Dynamic rear check endorsements that can be added to the endorsement format
const dynamicEndorsements = [
    {title: "Date", code: "date"},
    {title: "Terminal ID", code: "terminalId"},
    {title: "Source Location", code: "sourceLocation"},
    {title: "Source Institution Routing", code: "sourceInstitutionRouting"},
    {title: "Institution Name", code: "institutionName"},
    {title: "Session Number", code: "sessionNumber"},
];

/** @description - Component to manage back of check endorsements.
 * @param endorsement - The endorsement string.
 * @param index - The index of the endorsement.
 * @param handleEndorsementChange - Function to handle changes to the endorsement.
 * @param handleEndorsementDelete - Function to handle deletion of the endorsement by index.
 * @returns {JSX.Element}
 */
const EndorsementComponent = ({endorsement, index, handleEndorsementChange, handleEndorsementDelete}) => {
    const [inputValue, setInputValue] = useState(endorsement);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [filteredSuggestions, setFilteredSuggestions] = useState(dynamicEndorsements);
    const [errorMsg, setErrorMsg] = useState("");
    const textInputRef = useRef(null);

    // Set the input value to the matching endorsement index when it changes
    useEffect(() => {
        setInputValue(endorsement);
    }, [endorsement]);

    // Handle changes to the endorsement input
    const handleInputChange = (event) => {
        setErrorMsg("");
        const {value, selectionStart} = event.target;
        if (value.length > 40) {
            setErrorMsg("Endorsement must be 40 characters or less.");
            return;
        }

        setInputValue(value);
        setCursorPosition(selectionStart);
        handleEndorsementChange(index, value);

        const lastOpenBraceIndex = value.lastIndexOf("{", selectionStart - 1);
        const lastCloseBraceIndex = value.lastIndexOf("}", selectionStart - 1);

        // Check if the cursor is inside a brace to determine if we should show suggestions
        if (lastOpenBraceIndex !== -1 && (lastCloseBraceIndex === -1 || lastOpenBraceIndex > lastCloseBraceIndex)) {
            const textAfterBrace = value.slice(lastOpenBraceIndex + 1, selectionStart);
            const filtered = dynamicEndorsements.filter(({title, code}) =>
                title.toLowerCase().includes(textAfterBrace.toLowerCase()) ||
                code.toLowerCase().includes(textAfterBrace.toLowerCase())
            );
            setShowSuggestions(true);
            setFilteredSuggestions(filtered);
        } else {
            setShowSuggestions(false);
        }
    };

    // Handle selecting an endorsement option from the suggestions
    const handleSelectOption = (endorsementCode) => {
        let beforeCursor = inputValue.slice(0, cursorPosition);
        const afterCursor = inputValue.slice(cursorPosition);
        const lastBraceIndex = beforeCursor.lastIndexOf('{');
        beforeCursor = lastBraceIndex !== -1 ? beforeCursor.slice(0, lastBraceIndex + 1) : beforeCursor;

        const newTxtValue = beforeCursor + endorsementCode + "}" + afterCursor;
        setInputValue(newTxtValue);
        setShowSuggestions(false);
        handleEndorsementChange(index, newTxtValue);

        // Set the cursor position to the end of the inserted endorsement code. 
        // Timeout to avoid raced conditions and cursor not getting placed correctly.
        setTimeout(() => {
            if (textInputRef.current) {
                textInputRef.current.setSelectionRange(
                    beforeCursor.length + endorsementCode.length + 1,
                    beforeCursor.length + endorsementCode.length + 1
                );
                textInputRef.current.focus();
            }
        }, 0);
    };

    return (
        <Grid item>
            <Box sx={{
                display: 'flex',
                position: 'relative',
                justifyContent: 'flex-start',
                mt: 1,
                width: 'calc(100%-16px)'
            }}>
                {/* Endorsement TextField */}
                <TextField
                    label={`Line ${index + 1}`}
                    aria-label={`Line ${index + 1}`}
                    value={inputValue}
                    onChange={handleInputChange}
                    inputRef={textInputRef}
                    fullWidth
                    autoComplete="false"
                    error={errorMsg !== ""}
                    helperText={errorMsg}
                />
                {/* Suggestions popup list when brace is typed in */}
                {showSuggestions && (
                    <Paper sx={{
                        position: 'absolute',
                        top: textInputRef.current ? textInputRef.current.offsetTop + 50 : 0,
                        left: textInputRef.current ? textInputRef.current.offsetLeft : 0,
                        width: '80%',
                        zIndex: 10
                    }}>
                        <List>
                            {filteredSuggestions.map((option) => (
                                <ListItem key={option.code} sx={{m: 0, p: 0}}>
                                    <ListItemButton onClick={() => handleSelectOption(option.code)}
                                                    sx={{width: '100%', margin: 0, padding: 1}}
                                    >
                                        {option.title}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}
                {/* Delete Button */}
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <IconButton title={`Remove Line ${index + 1}`} onClick={() => handleEndorsementDelete(index)}
                                sx={{minWidth: '40px', minHeight: '40px', width: '40px', height: '40px'}}>
                        <DeleteIcon/>
                    </IconButton>
                </Box>
            </Box>
        </Grid>
    );
};

const CheckPreviewer = ({endorsements}) => {
    const [previewData, setPreviewData] = useState([])
    const [replacedEndorsements, setReplacedEndorsements] = useState([]);

    useEffect(() => {
        const fetchDataAndReplace = async () => {
            const {data} = await getCheckPreviewData();

            if (!data) {
                return;
            }

            setPreviewData(data)
        };

        fetchDataAndReplace();
    }, []);

    useEffect(() => {
        const replaced = endorsements.map(endorsement =>
            endorsement
                .replace(/{date}/g, new Date().toLocaleString('en-us', {hour12: true}).replace(',',''))
                .replace(/{terminalId}/g, previewData?.terminalId ?? "{terminalId}")
                .replace(/{sourceLocation}/g, previewData?.sourceLocation ?? "{sourceLocation}")
                .replace(/{sourceInstitutionRouting}/g, previewData?.sourceBankRouting ?? "{sourceInstitutionRouting}")
                .replace(/{institutionName}/g, previewData?.bankName ?? "{institutionName}")
                .replace(/{sessionNumber}/g, previewData?.sessionNumber ?? "{sessionNumber}")
        );
        setReplacedEndorsements(replaced);
    }, [endorsements, previewData]);

    return (
        <Grid item>
            <Box
                component={Paper}
                elevation={3}
                square
                sx={{
                    display: 'flex',
                    width: 600,
                    height: 275,
                    border: 1,
                    borderColor: 'black',
                    backgroundColor: '#F5F5F5',
                }}
            >
                {/* Left Section: Empty Box */}
                <Box
                    sx={{
                        width: '100px',
                        height: '90%',
                        border: 1,
                        borderColor: '#0000001F',
                        alignSelf: 'center',
                        margin: 2,
                    }}
                />

                {/* Middle Section: FI Added Endorsement Lines */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                    }}
                >
                    {replacedEndorsements.map((endorsement, index) => (
                        // Show endorsement line otherwise fallback to non-breaking space if empty endorsement
                        <Typography color="black" key={index}>{endorsement || "\u00A0"}</Typography>
                    ))}
                </Box>

                {/* Right Section: Customer "Endorse Here" Lines */}
                <Box
                    sx={{
                        display: 'flex',
                        height: '90%',
                        alignSelf: 'center',
                        margin: 2,
                        alignItems: 'center',
                    }}
                >
                    <Divider orientation="vertical" flexItem sx={{mx: 2, borderColor: "#0000001F"}}/>
                    <Divider orientation="vertical" flexItem sx={{mx: 2, borderColor: "#0000001F"}}/>
                    <Divider orientation="vertical" flexItem sx={{mx: 2, borderColor: "#0000001F"}}/>
                </Box>
            </Box>
        </Grid>
    )
}

export const AdminPanel = () => {
    const {customerConfigs, getCustomerConfigs} = useGlobalContext();
    const [isLoadingConfigs, setIsLoadingConfigs] = useState(true)
    const [isSavingConfigs, setIsSavingConfigs] = useState(false);
    const [blacklistLoading, setBlacklistLoading] = useState({
        accounts: false,
        routing: false,
    });
    const [trueChecksBlacklistLoading, setTrueChecksBlacklistLoading] = useState(false);
    const [newBlacklistDestAccount, setNewBlacklistDestAccount] = useState('');
    const [newBlacklistedRouting, setNewBlacklistedRouting] = useState('');
    const [newTrueChecksBlacklistedRouting, setNewTrueChecksBlacklistedRouting] = useState('');
    const [blackListDestAccounts, setBlackListDestAccounts] = useState([])
    const [blackListedRoutings, setBlacklistedRoutings] = useState([])
    const [trueChecksBlacklistedRoutings, setTrueChecksBlacklistedRoutings] = useState([])
    const [endorsements, setEndorsements] = useState([])
    const [clientCustomerConfigs, setClientCustomerConfigs] = useState({});
    const [isSaveConfigEnabled, setIsSaveConfigEnabled] = useState(false);
    const clientConfigRef = useRef();
    const utc = require("dayjs/plugin/utc");
    dayjs.extend(utc);

    useEffect(() => {
        loadBlacklistDestAccounts();
        loadBlacklistedRoutings();
    }, []);

    useEffect(() => {
        if (customerConfigs?.truechecksConfig?.enable) {
            setTrueChecksBlacklistedRoutings(customerConfigs?.trueChecksBlacklistedRoutings?.$values);
        }
    }, [customerConfigs]);

    useEffect(() => {
        loadCustomerConfigs()
    }, [customerConfigs]);

    useEffect(() => {
        setIsSaveConfigEnabled(validateConfigUpdates)
    }, [clientCustomerConfigs])

    const loadCustomerConfigs = async () => {
        setIsLoadingConfigs(true);
        try {
            if (Object.keys(customerConfigs).length === 0) {
                return;
            }
            const data = {...customerConfigs};
            delete data.truechecksConfig;// Remove TrueChecks from config object
            delete data.truechecksBlacklistedRoutings;// Remove TrueChecks blacklisted routings from config object
            const updatedConfig = {
                ...data,
                reviewTransactionThreshold: data.reviewTransactionThreshold.toFixed(2) ?? "0.00"
            };

            clientConfigRef.current = updatedConfig;

            if (data.endorsementFormat) {
                getEndorsements(data.endorsementFormat);
            }

            setClientCustomerConfigs(updatedConfig);
        } catch (error) {
            console.error("Error fetching customer config: ", error);
        } finally {
            if (Object.keys(clientCustomerConfigs).length > 0) {
                setIsLoadingConfigs(false);
            }
        }
    };

    const updateConfigs = async () => {
        setIsSavingConfigs(true);
        try {
            const {status, data} = await updateCustomerConfigs({
                ...clientCustomerConfigs,
                reviewTransactionThreshold: parseFloat(clientCustomerConfigs.reviewTransactionThreshold ?? 0.00)
            });
            const isSuccess = status === 200;
            const message = data?.message ?? (isSuccess ? "Updated configurations" : "Failed to update configurations");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'});
            await getCustomerConfigs();
        } catch {
            Snackbar({message: "An error occurred", variant: 'error'});
        } finally {
            setIsSavingConfigs(false);
        }
    };

    /** @description - Parse the endorsements within customer configs endorsement string
     * @param input - The endorsement string from the customer configs
     */
    function getEndorsements(input) {
        // Replace new line characters with literal \n so it goes through as a string and split by \n
        const lines = input.replaceAll("\n", "\\n").split("\\n");

        setEndorsements(lines);
    }

    /** @description - Handle changes to the endorsements
     * @param index - The index of the endorsement
     * @param value - The new value of the endorsement
     */
    function handleEndorsementChange(index, value) {
        const newEndorsements = [...endorsements];
        newEndorsements[index] = value;
        setEndorsements(newEndorsements);
    }

    /** @description - Add an endorsement to the endorsements list */
    const handleAddEndorsement = () => {
        setEndorsements([...endorsements, ""]);
    };

    /** @description - Delete an endorsement from the endorsements list
     * @param index - The index of the endorsement to delete
     */
    const handleEndorsementDelete = (index) => {
        const newEndorsements = endorsements.filter((endorsement, i) => i !== index);
        setEndorsements(newEndorsements);
    };

    // Update the endorsement format when the endorsements change
    useEffect(() => {
        const newEndorsementFormat = endorsements.map((endorsement) => {
            return endorsement
        }).join("\\n");

        setClientCustomerConfigs({
            ...clientCustomerConfigs,
            endorsementFormat: newEndorsementFormat
        });
    }, [endorsements]);

    /**
     * @description - Loads the blacklisted accounts from the API.
     */
    const loadBlacklistDestAccounts = async () => {
        getBlacklistDestAccounts().then((data) => {
            setBlackListDestAccounts(data.data.$values);
        }).catch((error) => {
            console.error("Error fetching blacklisted accounts: ", error);
        });
    }

    /**
     * @description - Loads the blacklisted routing numbers from the API.
     */
    const loadBlacklistedRoutings = async () => {
        getBlacklistedRoutings().then((data) => {
            setBlacklistedRoutings(data.data.$values);
        }).catch((error) => {
            console.error("Error fetching blacklisted routing numbers: ", error);
        });
    }

    /**
     * @description - Handles the addition of a blacklisted account.
     */
    const handleAddBlacklistDestAccount = async () => {
        setBlacklistLoading({...blacklistLoading, accounts: true});
        try {
            const {status, data} = await addBlacklistDestAccount(newBlacklistDestAccount);
            const isSuccess = status === 200;
            const message = data?.message ?? (isSuccess ? "Added blacklisted account" : "Failed to add blacklisted account");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'});
            setNewBlacklistDestAccount('');
            await loadBlacklistDestAccounts();
        } catch {
            Snackbar({message: "An error occurred", variant: 'error'});
        } finally {
            setBlacklistLoading({...blacklistLoading, accounts: false});
        }
    }

    /**
     * @description - Handles the deletion of a blacklisted account.
     * @param account
     */
    const handleDeleteBlacklistDestAccount = async (account) => {
        setBlacklistLoading({...blacklistLoading, accounts: true});
        try {
            const response = await removeBlacklistDestAccount(account);
            const isSuccess = response.status === 200;
            const message = response.data?.message ?? (isSuccess ? "Deleted blacklisted account" : "Failed to delete blacklisted account");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'})
            await loadBlacklistDestAccounts();
        } catch (error) {
            Snackbar({message: "An error occurred", variant: 'error'});
        } finally {
            setBlacklistLoading({...blacklistLoading, accounts: false});
        }
    }

    /**
     * @description - Handles the addition of a blacklisted routing number.
     * @returns {Promise<void>}
     */
    const handleAddBlacklistedRouting = async () => {
        setBlacklistLoading({...blacklistLoading, routing: true});
        try {
            const {status, data} = await addBlacklistedRouting(newBlacklistedRouting.padStart(9, '0'));
            const isSuccess = status === 200;
            const message = data?.message ?? (isSuccess ? "Added blacklisted routing" : "Failed to add blacklisted routing");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'});
            setNewBlacklistedRouting('');
            await loadBlacklistedRoutings();
        } catch (error) {
            Snackbar({message: error?.data?.message ?? "An error occurred", variant: 'error'});
        } finally {
            setBlacklistLoading({...blacklistLoading, routing: false});
        }
    }

    /**
     * @description - Handles the addition of a TrueChecks blacklisted routing number.
     * @returns {Promise<void>}
     */
    const handleAddTrueChecksBlacklistedRouting = async () => {
        setTrueChecksBlacklistLoading(true);
        try {
            const {
                status,
                data
            } = await addTrueChecksBlacklistedRouting(newTrueChecksBlacklistedRouting.padStart(9, '0'));
            const isSuccess = status === 200;
            const message = data?.message ?? (isSuccess ? "Added TrueChecks blacklisted routing" : "Failed to add TrueChecks blacklisted routing");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'});
            setNewTrueChecksBlacklistedRouting('');
            await getCustomerConfigs();
        } catch (error) {
            Snackbar({message: error?.data?.message ?? "An error occurred", variant: 'error'});
        } finally {
            setTrueChecksBlacklistLoading(false);
        }
    }

    /**
     * @description - Handles the deletion of a blacklisted routing number.
     * @param routing - The routing number to delete.
     */
    const handleDeleteBlacklistedRouting = async (routing) => {
        setBlacklistLoading({...blacklistLoading, routing: true});
        try {
            const response = await removeBlacklistedRouting(routing);
            const isSuccess = response.status === 200;
            const message = response.data?.message ?? (isSuccess ? "Deleted blacklisted routing" : "Failed to delete blacklisted routing");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'})
            await loadBlacklistedRoutings();
        } catch (error) {
            Snackbar({message: "An error occurred", variant: 'error'});
        } finally {
            setBlacklistLoading({...blacklistLoading, routing: false});
        }
    }

    /**
     * @description - Handles the deletion of a TrueChecks blacklisted routing number.
     * @param routing - The routing number to delete.
     */
    const handleDeleteTrueChecksBlacklistedRouting = async (routing) => {
        setTrueChecksBlacklistLoading(true);
        try {
            const response = await removeTrueChecksBlacklistedRouting(routing);
            const isSuccess = response.status === 200;
            const message = response.data?.message ?? (isSuccess ? "Deleted TrueChecks blacklisted routing" : "Failed to delete TrueChecks blacklisted routing");
            Snackbar({message: message, variant: isSuccess ? 'success' : 'error'})
            setNewTrueChecksBlacklistedRouting('');
            await getCustomerConfigs();
        } catch (error) {
            Snackbar({message: "An error occurred", variant: 'error'});
        } finally {
            setTrueChecksBlacklistLoading(false);
        }
    }


    /**
     * @description - Check if save configs button should be disabled based on the current and ref configs.
     * @returns {boolean}
     */
    const validateConfigUpdates = () => {
        // Check if configs are identical
        if (JSON.stringify(clientCustomerConfigs) === JSON.stringify(clientConfigRef.current)) {
            return true;
        }

        // Check if thresholds are not strictly equal but parse the same value
        const currentThreshold = clientCustomerConfigs?.reviewTransactionThreshold;
        const refThreshold = clientConfigRef?.current?.reviewTransactionThreshold;

        if (
            currentThreshold !== refThreshold &&
            parseFloat(currentThreshold) === parseFloat(refThreshold)
        ) {
            return true;
        }

        // Check if threshold is empty or negative
        if (currentThreshold === '' || parseFloat(currentThreshold) < 0) {
            return true;
        }

        return false;
    }


    return (
        <>
            <title>Admin Panel</title>
            <Box
                sx={{
                    display: "grid",
                    background: "transparent",
                    width: "100%",
                    height: "100%",
                    rowGap: 1,
                }}
            >
                <Grid
                    sx={{
                        width: "auto",
                        borderRadius: 1,
                        backgroundColor: "primary.componentbg",
                        padding: 1,
                    }}
                >
                    <Typography variant='h5'>Admin Panel</Typography>
                    <Divider/>

                    {/*Institution Configs*/}
                    <Typography variant='h6' sx={{mt: 2}}>Institution Configurations</Typography>
                    <Typography variant='body2' color='textSecondary'>Fields marked as "Optional" can be left
                        blank.</Typography>

                    <Box>
                        {isLoadingConfigs ? (
                            // Create 7 skeleton loaders
                            <Grid container spacing={2} sx={{padding: 1, justifyContent: "flex-start"}}>
                                {[...Array(6)].map((_, index) => (
                                    <Grid item key={index} size={{xs: 12, sm: 6, lg: 4}}>
                                        <Skeleton variant="rounded" height={56}/>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Grid container spacing={2} sx={{padding: 1, justifyContent: "flex-start"}}>
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <TextField
                                        label="Review Transaction Threshold"
                                        variant="outlined"
                                        fullWidth
                                        type="number"
                                        required={true}
                                        value={clientCustomerConfigs?.reviewTransactionThreshold ?? 0.00}
                                        onChange={(event) => {
                                            let inputValue = event.target.value
                                                .replace(/[^0-9.]/g, '') // Remove any non-numeric characters
                                                .replace(/(\..*)\./g, '$1') // Remove any extra decimal points
                                                .replace(/^0+(?=\d)/, ''); // Remove leading zeros

                                            const [wholeNum, decimal] = inputValue.split('.');

                                            if (wholeNum.length > 6) {
                                                return;
                                            }

                                            if (decimal?.length > 2) {
                                                inputValue = `${wholeNum}.${decimal.slice(0, 2)}`; // Limit to 2 decimal places
                                            }

                                            setClientCustomerConfigs({
                                                ...clientCustomerConfigs,
                                                reviewTransactionThreshold: inputValue
                                            });
                                        }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        }}
                                        error={!clientCustomerConfigs?.reviewTransactionThreshold || parseFloat(clientCustomerConfigs?.reviewTransactionThreshold) < 0}
                                        helperText={!clientCustomerConfigs?.reviewTransactionThreshold || parseFloat(clientCustomerConfigs?.reviewTransactionThreshold) < 0 ? "Threshold must be greater than $0.00" : ""}
                                    />
                                </Grid>
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <TimePicker
                                            label="Business Hours Start"
                                            value={clientCustomerConfigs?.businessHoursStart ? dayjs.utc(clientCustomerConfigs.businessHoursStart, "HH:mm:ss").local() : null}
                                            onChange={(newValue) => {
                                                setClientCustomerConfigs({
                                                    ...clientCustomerConfigs,
                                                    businessHoursStart: dayjs.utc(newValue).format("HH:mm:ss")
                                                });
                                            }}
                                            sx={{width: "100%"}}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <TimePicker
                                            label="Business Hours End"
                                            value={clientCustomerConfigs?.businessHoursEnd ? dayjs.utc(clientCustomerConfigs?.businessHoursEnd, "HH:mm:ss").local() : null}
                                            onChange={(newValue) => {
                                                setClientCustomerConfigs({
                                                    ...clientCustomerConfigs,
                                                    businessHoursEnd: dayjs.utc(newValue).format("HH:mm:ss")
                                                });
                                            }}
                                            sx={{width: "100%"}}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <TextField
                                        label="Amount Import Type"
                                        variant="outlined"
                                        fullWidth
                                        select
                                        value={clientCustomerConfigs?.amountImportType ?? ''}
                                        onChange={(e) => setClientCustomerConfigs({
                                            ...clientCustomerConfigs,
                                            amountImportType: parseInt(e.target.value)
                                        })}
                                    >
                                        <MenuItem value={1}>Prefer CAR/LAR</MenuItem>
                                        <MenuItem value={2}>Prefer Consumer</MenuItem>
                                        <MenuItem value={3}>Zero Consumer</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <TextField
                                        label="Password Expiration In Days (Optional)"
                                        variant="outlined"
                                        fullWidth
                                        type="number"
                                        value={clientCustomerConfigs?.passwordExpirationFrequencyInDays ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? null : Math.max(1, Math.min(365, parseInt(e.target.value)));
                                            setClientCustomerConfigs({
                                                ...clientCustomerConfigs,
                                                passwordExpirationFrequencyInDays: value
                                            });
                                        }}
                                    />
                                </Grid>
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <TextField
                                        label="Approve With Invalid MICR"
                                        variant="outlined"
                                        select
                                        fullWidth
                                        value={clientCustomerConfigs?.canApproveWithInvalidMicr ?? ''}
                                        onChange={(e) => setClientCustomerConfigs({
                                            ...clientCustomerConfigs,
                                            canApproveWithInvalidMicr: e.target.value
                                        })}
                                    >
                                        <MenuItem value={true}>True</MenuItem>
                                        <MenuItem value={false}>False</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <TextField
                                        label="Suppress Cash Export"
                                        variant="outlined"
                                        select
                                        fullWidth
                                        value={clientCustomerConfigs?.suppressCashExport ?? ''}
                                        onChange={(e) => setClientCustomerConfigs({
                                            ...clientCustomerConfigs,
                                            suppressCashExport: e.target.value
                                        })}
                                    >
                                        <MenuItem value={true}>True</MenuItem>
                                        <MenuItem value={false}>False</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        )}

                        {/* Rear Check Endorsements */}
                        <Typography variant='h6' sx={{mt: 2}}>Rear Check Endorsements</Typography>
                        <Typography variant='body2' color='textSecondary' sx={{mb: 1}}>
                            Add up to 10 custom endorsements to be printed on the back of the check. Type custom
                            endorsements, use {"{"} for dynamic suggestions, or combine both on one line.
                        </Typography>

                        <Grid container sx={{padding: 1, alignItems: 'center'}}>
                            {/* Endorsements Column */}
                            <Grid item size={{xs: 6, lg: 4}}>
                                <Grid container spacing={1} direction="column">
                                    {isLoadingConfigs ? (
                                        [0, 1, 2, 3].map((index) => (
                                            <Grid item key={index}>
                                                <Skeleton variant="rounded" height={56}/>
                                            </Grid>
                                        ))
                                    ) : (
                                        endorsements.map((endorsement, index) => (
                                            <Grid item key={`endorsement-${index}`}>
                                                <EndorsementComponent
                                                    index={index}
                                                    endorsement={endorsement}
                                                    handleEndorsementChange={handleEndorsementChange}
                                                    handleEndorsementDelete={handleEndorsementDelete}
                                                />
                                            </Grid>
                                        ))
                                    )}
                                    <Grid item>
                                        <Button
                                            variant="text"
                                            onClick={handleAddEndorsement}
                                            sx={{mt: 1, alignSelf: 'center'}}
                                            disabled={endorsements.length >= 10}
                                        >
                                            <AddIcon/> Add Endorsement
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Check Preview Column */}
                            <Grid item size={{md: 12, lg: 8}}
                                  sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                <Typography variant="body2" sx={{mb: 1}}>Check Endorsements Previewer</Typography>
                                <CheckPreviewer endorsements={endorsements}/>
                            </Grid>
                        </Grid>

                        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                            <LoadingButton
                                variant="contained"
                                loading={isSavingConfigs}
                                onClick={updateConfigs}
                                disabled={isSaveConfigEnabled}
                                sx={{
                                    display: 'flex',
                                    mr: 1,
                                    mt: 1
                                }}
                            >
                                Save Configurations
                            </LoadingButton>
                        </Box>
                    </Box>

                    {/* Branches and Terminal IDs */}
                    <Typography variant='h6' sx={{mt: 2}}>Branches and Terminal IDs</Typography>
                    <Typography variant='body2' color='textSecondary'>Manage branches and terminal IDs by dragging and
                        dropping terminals between branches.</Typography>
                    <BranchSources/>

                    {/* Blacklisted Accounts */}
                    <Typography variant='h6' sx={{mt: 2}}>Blacklisted Accounts and Routing Numbers</Typography>
                    <Typography variant='body2' color='textSecondary'>Manage blacklisted accounts and routing numbers by
                        searching, adding, or
                        removing them to enforce transaction reviews.</Typography>

                    <Grid container spacing={2} sx={{padding: 1, justifyContent: "flex-start"}}>
                        {/* Blacklisted Account Numbers */}
                        <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                            <Box sx={{display: 'flex', justifyContent: 'flex-start', mt: 1, width: 'calc(100%-16px)'}}>
                                <Autocomplete
                                    disableCloseOnSelect
                                    freeSolo
                                    forcePopupIcon={true}
                                    inputValue={newBlacklistDestAccount}
                                    options={blackListDestAccounts}
                                    getOptionLabel={(option) => option.toString()}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search or Add Blacklisted Destination Accounts"
                                            variant="outlined"
                                            fullWidth
                                            onChange={(e) => {
                                                // Remove any non-numeric characters from the input value
                                                const cleanAcct = e.target.value.replace(/\D/g, '').slice(0, 50);
                                                setNewBlacklistDestAccount(cleanAcct);
                                            }}
                                            InputLabelProps={{
                                                sx: {paddingRight: 2}
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <ListItem
                                            sx={{fontVariantNumeric: 'tabular-nums'}}
                                            {...props}
                                            secondaryAction={
                                                <IconButton edge="end"
                                                            title="Remove From Blacklist"
                                                            onClick={() => handleDeleteBlacklistDestAccount(option)}>
                                                    <DeleteIcon/>
                                                </IconButton>
                                            }
                                        >
                                            {option}
                                        </ListItem>
                                    )}
                                    filterOptions={(options, state) => {
                                        const inputValue = state.inputValue.toLowerCase();
                                        return options
                                            .filter(option => option.includes(inputValue))
                                            .sort((a, b) => Number(a) - Number(b));
                                    }}
                                    sx={{flexGrow: 1}}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddBlacklistDestAccount();
                                        }
                                    }}
                                    onInputChange={(event, newValue, reason) => {
                                        if (reason === "clear") {
                                            setNewBlacklistDestAccount("");
                                        } else {
                                            setNewBlacklistDestAccount(newValue.replace(/\D/g, '').slice(0, 50));
                                        }
                                    }}
                                />
                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                    <LoadingButton
                                        variant="contained"
                                        loading={blacklistLoading.accounts}
                                        onClick={handleAddBlacklistDestAccount}
                                        disabled={newBlacklistDestAccount === ''}
                                        sx={{
                                            minWidth: '40px',
                                            minHeight: '40px',
                                            width: '40px',
                                            height: '40px',
                                            ml: 1,
                                        }}
                                        title="Add Blacklisted Account"
                                    >
                                        <AddIcon/>
                                    </LoadingButton>
                                </Box>
                            </Box>
                        </Grid>
                        {/* Blacklisted Routing Numbers */}
                        <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                            <Box sx={{display: 'flex', justifyContent: 'flex-start', mt: 1, width: 'calc(100%-16px)'}}>
                                <Autocomplete
                                    disableCloseOnSelect
                                    freeSolo
                                    forcePopupIcon={true}
                                    inputValue={newBlacklistedRouting}
                                    options={blackListedRoutings}
                                    getOptionLabel={(option) => option.toString()}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search or Add Blacklisted Routing"
                                            variant="outlined"
                                            fullWidth
                                            onChange={(e) => {
                                                // Remove any non-numeric characters from the input value and no more than 9 characters
                                                const cleanRouting = e.target.value.replace(/\D/g, '').slice(0, 9);
                                                setNewBlacklistedRouting(cleanRouting);
                                            }}
                                            InputLabelProps={{
                                                sx: {paddingRight: 2}
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <ListItem
                                            {...props}
                                            sx={{fontVariantNumeric: 'tabular-nums'}}
                                            secondaryAction={
                                                <IconButton edge="end"
                                                            title="Remove From Blacklist"
                                                            onClick={() => handleDeleteBlacklistedRouting(option)}>
                                                    <DeleteIcon/>
                                                </IconButton>
                                            }
                                        >
                                            {option}
                                        </ListItem>
                                    )}
                                    filterOptions={(options, state) => {
                                        const inputValue = state.inputValue.toLowerCase();
                                        return options
                                            .filter(option => option.includes(inputValue))
                                            .sort((a, b) => Number(a) - Number(b));
                                    }}
                                    sx={{flexGrow: 1}}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAddBlacklistedRouting();
                                        }
                                    }}
                                    onInputChange={(event, newValue, reason) => {
                                        if (reason === "clear") {
                                            setNewBlacklistedRouting("");
                                        } else {
                                            setNewBlacklistedRouting(newValue.replace(/\D/g, '').slice(0, 9));
                                        }
                                    }}
                                />
                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                    <LoadingButton
                                        variant="contained"
                                        loading={blacklistLoading.routing}
                                        onClick={handleAddBlacklistedRouting}
                                        disabled={newBlacklistedRouting === '' || newBlacklistedRouting.length > 9}
                                        sx={{
                                            minWidth: '40px',
                                            minHeight: '40px',
                                            width: '40px',
                                            height: '40px',
                                            ml: 1,
                                        }}
                                        title="Add Blacklisted Routing"
                                    >
                                        <AddIcon/>
                                    </LoadingButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {customerConfigs?.truechecksConfig?.enable && (
                        <>
                            {/** TrueChecks® Blacklisted */}
                            <Typography variant='h6' sx={{mt: 2}}>TrueChecks® Blacklisted Routing Numbers</Typography>
                            <Typography variant='body2' color='textSecondary'>Manage TrueChecks® blacklisted routing
                                numbers by searching, adding, or removing them to enforce transaction
                                reviews.</Typography>

                            <Grid container spacing={2} sx={{padding: 1, justifyContent: "flex-start"}}>
                                {/*TrueChecks® Blacklisted Routing Numbers */}
                                <Grid item size={{xs: 12, sm: 6, lg: 4}}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        mt: 1,
                                        width: 'calc(100%-16px)'
                                    }}>
                                        <Autocomplete
                                            disableCloseOnSelect
                                            freeSolo
                                            forcePopupIcon={true}
                                            inputValue={newTrueChecksBlacklistedRouting}
                                            options={trueChecksBlacklistedRoutings}
                                            getOptionLabel={(option) => option.toString()}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Search or Add TrueChecks Blacklisted Routing"
                                                    variant="outlined"
                                                    fullWidth
                                                    onChange={(e) => {
                                                        // Remove any non-numeric characters from the input value and no more than 9 characters
                                                        const cleanRouting = e.target.value.replace(/\D/g, '').slice(0, 9);
                                                        setNewTrueChecksBlacklistedRouting(cleanRouting);
                                                    }}
                                                    InputLabelProps={{
                                                        sx: {paddingRight: 2}
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <ListItem
                                                    {...props}
                                                    sx={{fontVariantNumeric: 'tabular-nums'}}
                                                    secondaryAction={
                                                        <IconButton edge="end"
                                                                    title="Remove From TrueChecks® Blacklist"
                                                                    onClick={() => handleDeleteTrueChecksBlacklistedRouting(option)}>
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    }
                                                >
                                                    {option}
                                                </ListItem>
                                            )}
                                            filterOptions={(options, state) => {
                                                const inputValue = state.inputValue.toLowerCase();
                                                return options
                                                    .filter(option => option.includes(inputValue))
                                                    .sort((a, b) => Number(a) - Number(b));
                                            }}
                                            sx={{flexGrow: 1}}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddTrueChecksBlacklistedRouting();
                                                }
                                            }}
                                            onInputChange={(event, newValue, reason) => {
                                                if (reason === "clear") {
                                                    setNewTrueChecksBlacklistedRouting("");
                                                } else {
                                                    setNewTrueChecksBlacklistedRouting(newValue.replace(/\D/g, '').slice(0, 9));
                                                }
                                            }}
                                        />
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <LoadingButton
                                                variant="contained"
                                                loading={trueChecksBlacklistLoading.routing}
                                                onClick={handleAddTrueChecksBlacklistedRouting}
                                                disabled={newTrueChecksBlacklistedRouting === '' || newTrueChecksBlacklistedRouting.length > 9}
                                                sx={{
                                                    minWidth: '40px',
                                                    minHeight: '40px',
                                                    width: '40px',
                                                    height: '40px',
                                                    ml: 1,
                                                }}
                                                title="Add TrueChecks Blacklisted Routing"
                                            >
                                                <AddIcon/>
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        </>
    )
}
